import { useCallback, useEffect, useRef, useState } from 'react'
import { io, type Socket } from 'socket.io-client'
import { IDentalChart } from '@dataTypes/chartingFeature.types'
import {
  StreamingVoicePhase,
  VOICE_INACTIVITY_TIMEOUT_MS,
} from '@hooks/useStreamingVoiceSession'
import {
  useVoiceTranscriptFeed,
  VoiceTranscriptEntry,
  VoiceTranscriptSnapshot,
} from '@hooks/useVoiceTranscriptFeed'
import { getAccessToken } from '@services/auth/accessTokenStore'
import { getEnv } from '@utils/env'
import {
  createDentalKeywordState,
  DentalKeywordState,
  DentalVoiceCursor,
} from '@utils/dental-voice-state.utils'
import { pcmToBase64 } from '@utils/perio-audio.utils'

type StopOptions = {
  preserveMessage?: boolean
  message?: string
}

type DentalVoiceReadyPayload = {
  sessionId: string
  model: string
  cursor: DentalVoiceCursor
  message: string
}

type DentalVoiceTranscriptPayload = {
  sessionId: string
  itemId: string
  text: string
  formatted?: string
  interpreted?: string
}

type DentalVoiceChartPatchPayload = {
  sessionId: string
  chart: IDentalChart
  cursor: DentalVoiceCursor
  message: string
  toothChanged?: number
  progress?: { current: number; total: number }
}

type DentalVoiceMessagePayload = {
  sessionId: string
  message: string
  cursor?: DentalVoiceCursor
  control?: 'cursor_update'
}

type DentalVoiceErrorPayload = {
  sessionId?: string
  message: string
  code?: string
}

const DENTAL_PROGRESS_HIGHLIGHT_LEAD_MS = 120

type DentalVoiceDebugTranscript = {
  command: string
  heard: string
}

type DentalVoiceDebugEntry = DentalVoiceDebugTranscript & {
  state: 'pending' | 'applying'
  failureTimer?: ReturnType<typeof setTimeout>
}

const dentalVoiceDebugResult = (
  transcript: DentalVoiceDebugTranscript,
  applied: boolean,
  error?: string,
): void => {
  if (process.env.NODE_ENV !== 'development') return
  // eslint-disable-next-line no-console -- Intentionally visible only in local development diagnostics.
  console.debug(
    [
      '[DentalVoice]',
      `Applied: ${applied ? 'Applied' : 'Not applied'}`,
      `Command: ${transcript.command}`,
      `Heard: ${transcript.heard}`,
      `Error: ${error ?? 'None'}`,
    ].join('\n'),
  )
}

const chartingBase = (): string => {
  const base = getEnv('REACT_APP_BUSINESS_LAYER_CHARTING').replace(/\/$/, '')
  if (!base) throw new Error('The charting service URL is not configured.')
  return base
}

const createSessionId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `dental_voice_${Date.now()}_${Math.random().toString(36).slice(2)}`
}

const mergeProgressiveToothPatch = (
  current: IDentalChart,
  incoming: IDentalChart,
  tooth: number,
): IDentalChart => {
  const target = String(tooth)
  const currentOtherTeeth = (current.dental_data ?? []).filter(
    (row) => String(row.tooth_number) !== target,
  )
  const incomingTargetTooth = (incoming.dental_data ?? []).filter(
    (row) => String(row.tooth_number) === target,
  )
  return {
    ...incoming,
    dental_data: [...currentOtherTeeth, ...incomingTargetTooth],
  }
}

export function useDentalStreamingVoice({
  enabled,
  contextKey,
  chart,
  onChart,
  onToothChange,
}: {
  enabled: boolean
  contextKey: string
  chart: IDentalChart
  onChart: (next: IDentalChart) => void
  onToothChange?: (tooth: number) => void
}): {
  phase: StreamingVoicePhase
  message: string
  toggle: () => void
  stop: () => void
  keyword: DentalKeywordState
  backlog: number
  recognized: string
  liveText: string
  history: VoiceTranscriptEntry[]
  queued: string[]
  subscribe: (listener: () => void) => () => void
  getSnapshot: () => VoiceTranscriptSnapshot
  selectCursor: (patch: Partial<DentalVoiceCursor>) => void
  replaceChart: (nextChart: IDentalChart) => void
} {
  const [phase, setPhase] = useState<StreamingVoicePhase>('idle')
  const [message, setMessage] = useState('')
  const [keyword, setKeyword] = useState(createDentalKeywordState)
  const [backlog, setBacklog] = useState(0)
  const feed = useVoiceTranscriptFeed()
  const feedRef = useRef(feed)
  feedRef.current = feed
  const latest = useRef({ chart, enabled, contextKey, onChart, onToothChange })
  latest.current = { chart, enabled, contextKey, onChart, onToothChange }
  const keywordRef = useRef(keyword)
  const generation = useRef(0)
  const running = useRef(false)
  const cancelProgressRef = useRef<() => void>(() => {})
  const manualCursorOverrideRef = useRef<number | null>(null)
  const resources = useRef<{
    stream?: MediaStream
    audio?: AudioContext
    node?: AudioWorkletNode
    socket?: Socket
    sessionId?: string
    inactivityWatchdog?: ReturnType<typeof setInterval>
  }>({})

  const updateKeyword = useCallback((next: DentalKeywordState): void => {
    keywordRef.current = next
    setKeyword(next)
  }, [])

  const stop = useCallback(
    (options?: StopOptions): void => {
      const wasRunning = running.current
      generation.current++
      running.current = false
      cancelProgressRef.current()
      cancelProgressRef.current = () => {}
      manualCursorOverrideRef.current = null
      const old = resources.current
      resources.current = {}
      if (old.socket && old.sessionId) {
        old.socket.emit('dental_voice:stop', { sessionId: old.sessionId })
      }
      old.socket?.removeAllListeners()
      old.socket?.disconnect()
      clearInterval(old.inactivityWatchdog)
      old.node?.disconnect()
      old.stream?.getTracks().forEach((track) => track.stop())
      void old.audio?.close()
      feedRef.current.discard()
      setBacklog(0)
      if (wasRunning) {
        updateKeyword({ ...keywordRef.current, paused: false })
        setPhase('idle')
        if (!options?.preserveMessage) {
          setMessage(
            options?.message ??
              'Stopped. Any unfinished phrase was not applied.',
          )
        }
      }
    },
    [updateKeyword],
  )

  useEffect(() => {
    return (): void => stop()
  }, [contextKey, enabled, stop])

  useEffect(() => {
    feedRef.current.discard()
    setMessage('')
    updateKeyword(createDentalKeywordState())
  }, [contextKey, updateKeyword])

  const start = async (): Promise<void> => {
    if (!latest.current.enabled || running.current) return
    running.current = true
    manualCursorOverrideRef.current = null
    feedRef.current.reset()
    setBacklog(0)
    updateKeyword(createDentalKeywordState(keywordRef.current.cursor))
    const gen = ++generation.current
    const key = latest.current.contextKey
    const sessionId = createSessionId()
    let proxyReady = false
    let lastVoiceDetectedAt = Date.now()
    let resultItemId: string | undefined
    const debugTranscripts: DentalVoiceDebugEntry[] = []
    const clearDebugFailure = (entry: DentalVoiceDebugEntry): void => {
      clearTimeout(entry.failureTimer)
      entry.failureTimer = undefined
    }
    const completeDebugTranscript = (
      entry: DentalVoiceDebugEntry | undefined,
      applied: boolean,
      error?: string,
    ): void => {
      if (!entry) return
      clearDebugFailure(entry)
      const index = debugTranscripts.indexOf(entry)
      if (index >= 0) debugTranscripts.splice(index, 1)
      dentalVoiceDebugResult(entry, applied, error)
    }
    const scheduleDebugFailure = (error: string): void => {
      const entry = debugTranscripts.find(
        (candidate) => candidate.state === 'pending' && !candidate.failureTimer,
      )
      if (!entry) return
      entry.failureTimer = setTimeout(
        () => completeDebugTranscript(entry, false, error),
        250,
      )
    }
    const beginDebugApplication = (
      payload: DentalVoiceChartPatchPayload,
    ): DentalVoiceDebugEntry | undefined => {
      const continuingProgress =
        payload.progress && payload.progress.current > 1
      const entry = continuingProgress
        ? debugTranscripts.find((candidate) => candidate.state === 'applying')
        : debugTranscripts.find((candidate) => candidate.state === 'pending')
      if (entry) {
        clearDebugFailure(entry)
        entry.state = 'applying'
      }
      return entry
    }
    const valid = (): boolean =>
      gen === generation.current &&
      latest.current.enabled &&
      key === latest.current.contextKey
    const fail = (error: unknown): void => {
      if (!valid()) return
      const reason =
        error instanceof Error
          ? error.message
          : 'Unable to connect streaming transcription.'
      stop()
      setPhase('error')
      setMessage(reason)
    }
    const pendingPatches: Array<{
      payload: DentalVoiceChartPatchPayload
      debugEntry?: DentalVoiceDebugEntry
    }> = []
    let drainingPatches = false
    let patchQueueVersion = 0
    cancelProgressRef.current = (): void => {
      debugTranscripts.forEach(clearDebugFailure)
      debugTranscripts.length = 0
      patchQueueVersion++
      pendingPatches.length = 0
    }
    const showPatchTooth = (payload: DentalVoiceChartPatchPayload): void => {
      const manualTooth = manualCursorOverrideRef.current
      if (manualTooth !== null) {
        updateKeyword({
          ...keywordRef.current,
          cursor: { tooth: manualTooth },
        })
        return
      }
      updateKeyword({ ...keywordRef.current, cursor: payload.cursor })
      if (payload.toothChanged !== undefined) {
        latest.current.onToothChange?.(payload.toothChanged)
      }
    }
    const commitPatch = (
      payload: DentalVoiceChartPatchPayload,
      debugEntry?: DentalVoiceDebugEntry,
    ): void => {
      const nextChart =
        payload.progress && payload.toothChanged !== undefined
          ? mergeProgressiveToothPatch(
              latest.current.chart,
              payload.chart,
              payload.toothChanged,
            )
          : payload.chart
      latest.current.chart = nextChart
      latest.current.onChart(nextChart)
      setMessage(payload.message)
      feedRef.current.annotate({
        id: resultItemId,
        note: payload.message,
        status: 'applied',
      })
      if (
        !payload.progress ||
        payload.progress.current >= payload.progress.total
      ) {
        completeDebugTranscript(debugEntry, true)
        resultItemId = undefined
      }
    }
    const waitForHighlightLead = async (): Promise<void> => {
      await new Promise<void>((resolve) => {
        if (typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(() => resolve())
          return
        }
        setTimeout(resolve, 0)
      })
      await new Promise<void>((resolve) =>
        setTimeout(resolve, DENTAL_PROGRESS_HIGHLIGHT_LEAD_MS),
      )
    }
    const drainPatches = async (): Promise<void> => {
      if (drainingPatches) return
      drainingPatches = true
      const version = patchQueueVersion
      try {
        while (
          valid() &&
          version === patchQueueVersion &&
          pendingPatches.length
        ) {
          const next = pendingPatches.shift()
          if (!next) break
          const { payload, debugEntry } = next
          showPatchTooth(payload)
          await waitForHighlightLead()
          if (!valid() || version !== patchQueueVersion) return
          commitPatch(payload, debugEntry)
        }
      } finally {
        drainingPatches = false
        if (valid() && pendingPatches.length) void drainPatches()
      }
    }

    setPhase('connecting')
    setMessage('Connecting streaming transcription...')
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error(
          'Microphone capture is unavailable. Use a supported browser over HTTPS (or localhost for development).',
        )
      }
      const token = getAccessToken()
      const dbName = localStorage.getItem('db_name')
      if (!token) throw new Error('Sign in again before using Dental voice.')
      const socket = io(chartingBase(), {
        path: '/api/v1/charting/socket.io/',
        transports: ['websocket'],
        reconnection: false,
        autoConnect: false,
        auth: {
          token,
          dbName: dbName ?? undefined,
        },
      })
      resources.current.socket = socket
      resources.current.sessionId = sessionId

      socket.on('connect_error', (error) => fail(error))
      socket.on('disconnect', (reason) => {
        if (running.current && valid()) {
          fail(new Error(`Voice connection closed: ${reason}`))
        }
      })
      socket.on('dental_voice:ready', (payload: DentalVoiceReadyPayload) => {
        if (!valid() || payload.sessionId !== sessionId) return
        proxyReady = true
        lastVoiceDetectedAt = Date.now()
        updateKeyword(createDentalKeywordState(payload.cursor))
        setPhase('listening')
        setMessage(payload.message)
      })
      socket.on(
        'dental_voice:partial',
        (payload: DentalVoiceTranscriptPayload) => {
          if (!valid() || payload.sessionId !== sessionId) return
          feedRef.current.partial(payload.itemId, payload.text)
        },
      )
      socket.on(
        'dental_voice:final',
        (payload: DentalVoiceTranscriptPayload) => {
          if (!valid() || payload.sessionId !== sessionId) return
          debugTranscripts.push({
            command: payload.interpreted ?? payload.text,
            heard: payload.text,
            state: 'pending',
          })
          feedRef.current.finalize(
            payload.itemId,
            payload.interpreted ?? payload.text,
            { heard: payload.text },
          )
          setBacklog((value) => Math.max(0, value - 1))
        },
      )
      socket.on(
        'dental_voice:message',
        (payload: DentalVoiceMessagePayload) => {
          if (!valid() || payload.sessionId !== sessionId) return
          if (payload.control !== 'cursor_update') {
            manualCursorOverrideRef.current = null
          }
          setMessage(payload.message)
          if (payload.control !== 'cursor_update') {
            scheduleDebugFailure(payload.message)
          }
          resultItemId = feedRef.current.annotate({
            note: payload.message,
            status: 'failed',
          })
          if (payload.cursor) {
            const tooth =
              manualCursorOverrideRef.current ?? payload.cursor.tooth
            updateKeyword({
              ...keywordRef.current,
              cursor: { tooth },
            })
          }
        },
      )
      socket.on(
        'dental_voice:chart_patch',
        (payload: DentalVoiceChartPatchPayload) => {
          if (!valid() || payload.sessionId !== sessionId) return
          const debugEntry = beginDebugApplication(payload)
          if (payload.toothChanged !== undefined) {
            pendingPatches.push({ payload, debugEntry })
            void drainPatches()
            return
          }
          showPatchTooth(payload)
          commitPatch(payload, debugEntry)
        },
      )
      socket.on('dental_voice:error', (payload: DentalVoiceErrorPayload) => {
        if (!valid()) return
        if (payload.sessionId && payload.sessionId !== sessionId) return
        completeDebugTranscript(
          debugTranscripts.find((entry) => entry.state === 'pending'),
          false,
          payload.message,
        )
        fail(new Error(payload.message))
      })
      socket.on(
        'dental_voice:stopped',
        (payload: { sessionId: string; reason: string }) => {
          if (!valid() || payload.sessionId !== sessionId) return
          stop({
            preserveMessage: payload.reason === 'voice-command',
            message: 'Microphone stopped. Click the microphone to start again.',
          })
        },
      )

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      })
      if (!valid()) {
        stream.getTracks().forEach((track) => track.stop())
        return
      }
      resources.current.stream = stream
      const audio = new AudioContext({ sampleRate: 24000 })
      resources.current.audio = audio
      await audio.audioWorklet.addModule('/perio-stream-capture.js')
      if (!valid()) return
      if (audio.sampleRate !== 24000) {
        throw new Error(
          'This browser cannot capture at the required sample rate.',
        )
      }
      socket.on('connect', () => {
        if (!valid()) return
        socket.emit('dental_voice:start', {
          sessionId,
          patient_id: latest.current.chart.patient_id,
          clinic_id: latest.current.chart.clinic_id,
          chart: latest.current.chart,
          cursor: keywordRef.current.cursor,
        })
      })
      const node = new AudioWorkletNode(audio, 'perio-stream-capture')
      resources.current.node = node
      node.onprocessorerror = (): void =>
        fail(
          new Error(
            'Audio capture failed. Restart and repeat unfinished speech.',
          ),
        )
      const mute = audio.createGain()
      mute.gain.value = 0
      node.port.onmessage = (
        event: MessageEvent<{
          type: string
          audio?: Float32Array
          error?: string
        }>,
      ): void => {
        if (!valid()) return
        if (!proxyReady) return
        try {
          if (event.data.type === 'error') throw new Error(event.data.error)
          if (event.data.type === 'start') {
            lastVoiceDetectedAt = Date.now()
          } else if (event.data.type === 'audio' && event.data.audio) {
            socket.emit('dental_voice:audio.append', {
              sessionId,
              audio: pcmToBase64(event.data.audio),
            })
          } else if (event.data.type === 'commit') {
            setBacklog((value) => value + 1)
            socket.emit('dental_voice:audio.commit', { sessionId })
          }
        } catch (error) {
          fail(error)
        }
      }
      audio.createMediaStreamSource(stream).connect(node)
      node.connect(mute).connect(audio.destination)
      await audio.resume()
      socket.connect()
      resources.current.inactivityWatchdog = setInterval(() => {
        if (
          valid() &&
          proxyReady &&
          Date.now() - lastVoiceDetectedAt >= VOICE_INACTIVITY_TIMEOUT_MS
        ) {
          stop({
            message:
              'Microphone stopped after 3 minutes without detected speech. Click the microphone to start again.',
          })
        }
      }, 1000)
      stream.getTracks().forEach((track) => {
        track.onended = (): void =>
          fail(new Error('Microphone disconnected. Restart to continue.'))
      })
    } catch (error) {
      fail(error)
    }
  }

  const toggle = (): void => {
    if (running.current) stop()
    else void start()
  }

  const selectCursor = useCallback(
    (patch: Partial<DentalVoiceCursor>): void => {
      const nextCursor = { ...keywordRef.current.cursor, ...patch }
      if (keywordRef.current.cursor.tooth === nextCursor.tooth) return
      cancelProgressRef.current()
      manualCursorOverrideRef.current = nextCursor.tooth
      updateKeyword({
        ...keywordRef.current,
        cursor: nextCursor,
      })
      setBacklog(0)
      feedRef.current.discard()
      const sessionId = resources.current.sessionId
      if (running.current && resources.current.socket && sessionId) {
        resources.current.socket.emit('dental_voice:cursor.update', {
          sessionId,
          tooth: nextCursor.tooth,
        })
        setMessage(
          `Position changed to tooth ${nextCursor.tooth}. Unfinished speech was discarded. Still listening - pause briefly and repeat any unfinished phrase.`,
        )
      }
    },
    [updateKeyword],
  )

  const replaceChart = useCallback((nextChart: IDentalChart): void => {
    cancelProgressRef.current()
    latest.current.chart = nextChart
    feedRef.current.reset()
    setBacklog(0)
    const sessionId = resources.current.sessionId
    if (running.current && resources.current.socket && sessionId) {
      resources.current.socket.emit('dental_voice:chart.replace', {
        sessionId,
        chart: nextChart,
      })
      setMessage(
        `Chart reset. Still listening at tooth ${keywordRef.current.cursor.tooth}.`,
      )
    }
  }, [])

  return {
    phase,
    message,
    toggle,
    stop,
    keyword,
    backlog,
    get recognized(): string {
      return feed.recognized
    },
    get liveText(): string {
      return feed.liveText
    },
    get history(): VoiceTranscriptEntry[] {
      return feed.history
    },
    get queued(): string[] {
      return feed.queued
    },
    subscribe: feed.subscribe,
    getSnapshot: feed.getSnapshot,
    selectCursor,
    replaceChart,
  }
}
